import { ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, type FormInstance, Skeleton, Typography } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../../AppLayout.tsx";
import type { RootState } from "../../store.ts";
import * as commonColumns from "../../../common/columns.tsx";
import { Card } from "../../../common/components/Card.tsx";
import { ListActionBar } from "../../../common/components/ListActionBar.tsx";
import { NoData } from "../../../common/components/NoDataComp.tsx";
import { PageTitleContainer } from "../../../common/components/PageTitle.tsx";
import {
	TableWrapper,
	type PaginationAndFilter,
	type TableWrapperProps,
} from "../../../common/components/Table.tsx";
import { clientFilter } from "../../common/filter.ts";
import { sanitizedFilter } from "../../../common/functions.ts";
import { routeNames } from "../../common/routes.ts";
import { clientActions } from "../../../features/client/clientSlice.ts";
import type {
	Client,
	CurrentUser,
	PaginatedClientList,
} from "../../generated-axios-client/index.ts";

const onFilter =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		form
			.validateFields()
			.then((values) => {
				dispatch(
					routerActions.navigateTo(
						currentRoute.name,
						sanitizedFilter({ ...currentRoute.params, ...values, page: 1 }),
					),
				);
			})
			.catch(() => {});
	};

const onCancel =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		dispatch(
			routerActions.navigateTo(
				currentRoute.name,
				sanitizedFilter({
					...currentRoute.params,
					...clientFilter,
					page: 1,
				}),
			),
		);
		form.resetFields();
	};

type rowDataType = {
	user?: CurrentUser;
	results: PaginatedClientList;
};

const getColumns: TableWrapperProps<
	PaginatedClientList,
	AnyObject
>["getColumns"] = (intl, currentFilter) => [
	commonColumns.simpleColumn("row_number", "table_column_ticket_number", intl),
	commonColumns.simpleColumn(
		"legal_name",
		"client_table_column_name",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"tax_id",
		"client_table_column_tax",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"contact_name",
		"client_table_column_contact_name",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"contact_email",
		"client_table_column_contact_email",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"contact_phone",
		"client_table_column_contact_phone",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"updated_at",
		"client_table_column_last_updated",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn("actions", "table_column_actions", intl),
];

const getTableRows: TableWrapperProps<rowDataType, AnyObject>["getRows"] = (
	intl,
	dispatch,
	data,
	modalApi,
) => {
	const { results, user } = data;

	return Object.values(results.results).map((client: Client, index: number) => {
		const showDeleteConfirm = () => {
			modalApi?.confirm({
				title: intl.formatMessage({ id: "client_delete_confirmation" }),
				icon: <ExclamationCircleFilled />,
				okText: intl.formatMessage({ id: "confirm" }),
				okType: "danger",
				cancelText: intl.formatMessage({ id: "cancel" }),
				onOk() {
					if (client.id) {
						dispatch(clientActions.deleteClient(client.id));
					}
				},
				onCancel() {},
			});
		};

		const onView = () => {
			dispatch(
				routerActions.navigateTo(routeNames.CLIENT_VIEW, { id: client.id }),
			);
		};

		const onEdit = () => {
			dispatch(
				routerActions.navigateTo(routeNames.CLIENT_EDIT, { id: client.id }),
			);
		};
		const onDelete = () => {
			showDeleteConfirm();
		};
		return {
			key: client.id,
			row_number: index + 1,
			legal_name: client.id ? (
				<Typography.Link onClick={onView}>{client.legal_name}</Typography.Link>
			) : (
				client.legal_name
			),
			tax_id: client.tax_id,
			contact_name: client.contact_name,
			contact_email: client.contact_email,
			contact_phone: client.contact_phone,
			updated_at: client.updated_at ? (
				commonColumns.dateValue(client.updated_at, intl)
			) : (
				<NoData intl={intl} />
			),
			actions: commonColumns.actionsValue(
				client,
				undefined,
				undefined,
				user,
				onView,
				onEdit,
				onDelete,
				undefined,
				"Client",
			),
		};
	});
};

type ClientsFilterTitleProps = {
	dispatch: Dispatch;
	currentRoute: RouterState;
	form: FormInstance;
	intl: IntlShape<string>;
};

const FacilityFilterTitle = (props: ClientsFilterTitleProps) => {
	const { form, dispatch, currentRoute, intl } = props;

	return (
		<ListActionBar
			intl={intl}
			form={form}
			filterDisabled={true}
			onFilter={onFilter(dispatch, form, currentRoute)}
			onCancel={onCancel(dispatch, form, currentRoute)}
			filterTemplate={clientFilter}
			currentFilter={currentRoute.params}
			searchBarPlaceholderKey={intl.formatMessage({
				id: "client_table_header_search_placeholder",
			})}
		/>
	);
};

type ClientsPageProps = ReturnType<typeof withClients> & DispatchProp;
export const ClientsPage = (props: ClientsPageProps) => {
	const { results, dispatch, currentRoute, intl, user, modalAPI } = props;

	const rowData: rowDataType = {
		user,
		results,
	};

	const pagination = results.pagination;

	const [form] = Form.useForm();

	if (!currentRoute) {
		return <Skeleton active />;
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "client_table_header_name" })}
				subtitle={intl.formatMessage(
					{ id: "client_table_header_number_of_items" },
					{ no: results.pagination.total_items },
				)}
				extra={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() =>
							dispatch(routerActions.navigateTo(routeNames.CLIENT_CREATE))
						}
					>
						{intl.formatMessage({ id: "client_create_button" })}
					</Button>
				}
			>
				<Card
					title={
						<FacilityFilterTitle
							intl={intl}
							form={form}
							dispatch={dispatch}
							currentRoute={currentRoute}
						/>
					}
				>
					<TableWrapper
						data={rowData}
						getRows={getTableRows}
						getColumns={getColumns}
						intl={intl}
						route={currentRoute}
						modalApi={modalAPI}
						applyFilterAction={(filterParams) =>
							routerActions.navigateTo(routeNames.CLIENT_LIST, filterParams)
						}
						pagination={pagination as PaginationAndFilter}
					/>
				</Card>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
};

const withClients = (state: RootState) => ({
	results: state.client.list,
	currentRoute: state.router.route,
	intl: state.intl.datetimeIntl,
	modalAPI: state.appFeedback.modal,
	user: state.userstate.user,
});
export const ConnectedClientsPage = connect(withClients)(ClientsPage);

export default ConnectedClientsPage;
